import { ChartLineIcon, DownloadIcon, TableIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { type CsvValue, downloadCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

export interface Column {
  key: string;
  label: string;
  /** How the table view shows the value; the CSV keeps it as the API sent it. */
  format?: (value: CsvValue) => string;
  numeric?: boolean;
}

export type Row = Record<string, CsvValue>;

/** The rows as a plain table: every chart's twin, readable without hovering. */
export function DataTable({
  columns,
  rows,
  caption,
}: {
  columns: readonly Column[];
  rows: readonly Row[];
  caption?: string;
}) {
  return (
    <div className="max-h-96 scrollbar-thin overflow-auto">
      <table className="w-full text-sm">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead className="sticky top-0 bg-surface">
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  "px-2 py-1.5 font-medium whitespace-nowrap",
                  column.numeric && "text-right",
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own
            <tr key={i} className="border-b border-border/60 last:border-0">
              {columns.map((column) => {
                const value = row[column.key];
                return (
                  <td
                    key={column.key}
                    className={cn(
                      "px-2 py-1.5 whitespace-nowrap",
                      column.numeric && "text-right tabular",
                    )}
                  >
                    {column.format ? column.format(value) : (value ?? "–")}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A figure: its title and what it is over, the chart or its table, and CSV export of the same
 * rows. Every chart has the table, so no value hides behind a tooltip or a colour.
 */
export function ChartFrame({
  title,
  description,
  csvName,
  columns,
  rows,
  children,
  className,
  aside,
}: {
  title: string;
  /** What the figure is over, and how to read it. */
  description?: ReactNode;
  csvName: string;
  columns: readonly Column[];
  rows: readonly Row[];
  children: ReactNode;
  className?: string;
  /** Next to the title, e.g. the current value. */
  aside?: ReactNode;
}) {
  const { t } = useTranslation();
  const [table, setTable] = useState(false);
  const titleId = useId();
  return (
    <Card className={cn("min-w-0 p-4", className)}>
      <figure aria-labelledby={titleId} className="m-0">
        <div className="mb-3 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <figcaption id={titleId} className="text-sm font-semibold tracking-tight">
              {title}
            </figcaption>
            {description ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
            ) : null}
          </div>
          {aside}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-pressed={table}
            aria-label={table ? t("adminApp.chart.showChart") : t("adminApp.chart.showTable")}
            title={table ? t("adminApp.chart.showChart") : t("adminApp.chart.showTable")}
            onClick={() => setTable((on) => !on)}
          >
            {table ? <ChartLineIcon /> : <TableIcon />}
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("adminApp.chart.exportCsv")}
            title={t("adminApp.chart.exportCsv")}
            onClick={() =>
              downloadCsv(
                csvName,
                columns.map((c) => c.label),
                rows.map((row) => columns.map((c) => row[c.key])),
              )
            }
          >
            <DownloadIcon />
          </Button>
        </div>
        {table ? <DataTable columns={columns} rows={rows} caption={title} /> : children}
      </figure>
    </Card>
  );
}
